import Vue from 'vue';
import axios from 'axios';
import orderBy from 'lodash/orderBy';
import union from 'lodash/union';
import each from 'lodash/each';
import find from 'lodash/find';
import pull from 'lodash/pull';
import nth from 'lodash/nth';
import moment from 'moment';

import { Plugins, FilesystemDirectory, FilesystemEncoding } from '@capacitor/core';
import { BASE_URL } from '@/constants/constants';

const { Storage, Filesystem, Device } = Plugins;
const rootUrl = BASE_URL;

export default {
    state: {
        documents: [],
        downloading: [],
        download_errors: [],
        fetching_all: false,
        list: [],
        old_list: [],
        new_list: [],
        cached: {},
        document_busy: false,
        fetching: false,
        annotations: {},
        dirty_documents: [], // local has new annotations, update the server.
        dirty_annotations: [], // server fetched new annotations, update locally.
        annotations_queue: [],
        last_saved: null,
        updater: false,
        document_syncing: false
    },

    mutations: {
        setDocumentsList(state, data) {
            // set list in store
            let list = orderBy(data, 'last_modified', 'desc');
            Vue.set(state, 'list', list);
            // commit to localStorage
            Storage.set({
                key: 'documents_list',
                value: JSON.stringify(state.list)
            });
        },

        setDownloadQueue(state, data) {
            Vue.set(state, 'downloading', data);
            try {
                Storage.set({
                    key: 'document_download_queue',
                    value: JSON.stringify(state.downloading)
                });
            } catch (e) {
                //
            }
        },

        clearDirtyAnnotations(state, data) {
            Vue.set(state, 'dirty_annotations', []);
        },

        clearUpdater(state) {
            window.clearTimeout(state.updater);
            state.updater = false;
        },

        setUpdater(state, data) {
            state.updater = data;
        },

        setLastSaved(state, data) {
            Vue.set(state, 'last_saved', data);
            try {
                Storage.set({
                    key: 'last_saved_annotation',
                    value: JSON.stringify(state.last_saved)
                });
            } catch (e) {
                //
            }
        },

        addDirtyAnnotation(state, data) {
            let list = union(data, state.dirty_annotations);
            Vue.set(state, 'dirty_annotations', list);
        },

        addToCache(state, {document_id, uri}) {
            Vue.set(state.cached, document_id, true);
            Storage.set({
                key: 'documents_cache',
                value: JSON.stringify(state.cached)
            });
        },

        setCache(state, data) {
            if (data && Array.isArray(data)) {
                state.cached = data;
            } else {
                state.cached = [];
            }
            Storage.set({
                key: 'documents_cache',
                value: JSON.stringify(state.cached)
            });
        },

        addToQueue(state, document_id) {
            let list = union(document_id, state.downloading);
            Vue.set(state, 'downloading', list);
            try {
                Storage.set({
                    key: 'document_download_queue',
                    value: JSON.stringify(state.downloading)
                });
            } catch (e) {
                //
            }
        },

        removeFromQueue(state, document_id) {
            let list = state.downloading;
            pull(list, document_id);
            Vue.set(state, 'downloading', list);
            try {
                Storage.set({
                    key: 'document_download_queue',
                    value: JSON.stringify(state.downloading)
                });
            } catch (e) {
                //
            }
        },

        removeFromDirtyDocuments(state, document_id) {
            let list = state.dirty_documents;
            pull(list, document_id);
            Vue.set(state, 'dirty_documents', list);
            Storage.set({
                key: 'dirty_documents',
                value: JSON.stringify(state.dirty_documents)
            });
        },

        clearDirtyAnnotation(state, data) {
            pull(state.dirty_annotations, data);
        },

        setDocumentBusy(state, data) {
            Vue.set(state, 'document_busy', data);
        },

        setDocumentSyncing(state, data) {
            Vue.set(state, 'document_syncing', data); 
        },

        setFetching(state, data) {
            Vue.set(state, 'fetching_all', data); 
        },

        addAnnotationState(state, { document_id, annotation, annotation_id }) {
            const new_annotation = { id: annotation_id, document: document_id, annotations: annotation, last_modified: Number(moment().format('x')) };
            Vue.set(state.annotations, document_id, new_annotation);
            Storage.set({
                key: 'annotations_list',
                value: JSON.stringify(state.annotations)
            });
            const exists = state.annotations_queue.findIndex(item => item.document === document_id );
            if (exists < 0)  {
                console.info("Adding to queue");
                state.annotations_queue.push(new_annotation);
            } else {
                console.info("Replacing in queue");
                Vue.set(state.annotations_queue, exists, new_annotation);
            }
        },

        shiftAnnotationState(state) {
            state.annotations_queue.shift();
        },

        updateAnnotation(state, annotation) {
            Vue.set(state.annotations, annotation.document, annotation);
            Storage.set({
                key: 'annotations_list',
                value: JSON.stringify(state.annotations)
            });
        },

        setAnnotationsList(state, annotations) {
            Vue.set(state, 'annotations', annotations);
            Storage.set({
                key: 'annotations_list',
                value: JSON.stringify(annotations)
            });
        },

        setCurrentDocument(state, data) {
            Vue.set(state, 'current_document', data);
        },

        setDirtyDocuments(state, data) {
            Vue.set(state, 'dirty_documents', data);
        },

        setCurrentDocumentAnnotation(state, data) {
            Vue.set(state, 'current_annotation', data);
        },

        setDocumentUri(state, data) {
            Vue.set(state, 'pdf_uri', data);
        },

        setDocuments(state, data) {
            // set new_list that's used to compare w/ old_list for document content updates
            Vue.set(state, 'documents', data);
        },

        setNewDocumentsList(state, data) {
            // set new_list that's used to compare w/ old_list for document content updates
            let new_list = orderBy(data, 'last_modified', 'desc');
            Vue.set(state, 'new_list', new_list);

        },

        setOldDocumentsList(state, data) {
            // set old_list that's used to compare w/ new_list for document content updates
            let old_list = orderBy(data, 'last_modified', 'desc');
            Vue.set(state, 'old_list', old_list);            
        },

        setState(state, data) {
            Vue.set(state, 'stat', data);
        },

        addDocument(state, data) {
            let cached = { ...state.cached, ...data };
            Vue.set(state, 'cached', cached);
        },

    },

    actions: {
        getDocumentsData({ commit, dispatch }) {
            // remeber what we have cached
            Storage.get({ key: 'documents_cache' }).then((response) => {
                const dirty = JSON.parse(response.value);
                commit('setCache', dirty);
            });
            // gets documents_list from local storage
            Storage.get({ key: 'documents_list' }).then((response) => {
                let list = JSON.parse(response.value);
                if (list && list.length) {
                    commit('setDocumentsList', list);
                    commit('setOldDocumentsList', list);
                }
                dispatch('fetchDocuments');
            });
            // get cached annotations from local storage
            Storage.get({ key: 'annotations_list' }).then((response) => {
                const annotations = JSON.parse(response.value);
                if (annotations) {
                    commit('setAnnotationsList', annotations);
                }
            });
            // get documents which are pending a server update from storage
            Storage.get({ key: 'dirty_documents' }).then((response) => {
                const dirty = JSON.parse(response.value);
                if (dirty && dirty.length) {
                    commit('setDirtyDocuments', dirty);
                }
            });
        },

        getAnnotationsList({ commit, dispatch, getters, rootState }) {
            Storage.get({ key: 'annotations_list' }).then((response) => {
                let list = JSON.parse(response.value);
                if (list && list.length) {
                    commit('setAnnotationsList', list);
                }
            }).finally(() => {
                if (rootState.online_status) { 
                    dispatch('fetchAnnotations');
                }
            });
        },

        clearAnnotationsList({ commit, dispatch }) {
            commit('setAnnotationsList', []);
        },
        
        async fetchAnnotations({ state, commit, dispatch, rootState }) { 
            // fetch new document list            
            commit('setDocumentBusy', true);
            try {
                const headers = { 'Authorization': rootState.authorization };
                const response = await axios.get(`${rootUrl}/account/annotations/author/`, { headers: headers });
                const list = response.data;
                commit('setAnnotationsList', list);
                commit('setDocumentBusy', false);
                return Promise.resolve(list);
            } catch (e) {
                console.error(e);
                commit('setDocumentBusy', false);
                return Promise.reject(e);
            }
        },

        processAnnotationQueue({ state, commit, rootState, getters, dispatch }) {
            if (state.annotations_queue.length < 1) { return false; }
            if (state.updater) { return false; }
            
            const next = getters.getAnnotationState;
            if (!next) { return false; }
            
            commit('setUpdater', setTimeout(async () => {
                console.info("Sending annotation in queue");
                if (rootState.authorization) {
                    commit('setDocumentBusy', true);
                    const headers = { 'Authorization': rootState.authorization };
                    try {
                        const data = { document: next.document, json: JSON.stringify(next.annotations) }
                        const response = await axios.post(`${rootUrl}/account/annotations/author/`, data, { headers: headers });
                        if (response.status === 200 || response.status === 201) {
                            commit('setLastSaved', response.data);
                            commit('updateAnnotation', response.data);
                            dispatch('getAnnotationsList');
                        } else {
                            console.error(response);
                        }
                    } catch (e) {
                        console.error(e);
                    }
                    commit('setDocumentBusy', false);
                    commit('shiftAnnotationState');
                    commit('clearUpdater');
                    // Check the queue again;
                    dispatch('processAnnotationQueue');
                }
            }, 1000));
        },

        async fetchDocuments({ state, commit, dispatch, rootState }) {
            // fetch new document list
            const headers = { 'Authorization': rootState.authorization };
            const response = await axios.get(`${rootUrl}/account/application/2/subscription_document`, { headers: headers });            
            
            let list = response.data;
            commit('setDocumentsList', list);
            
            if (state.old_list && state.old_list.length) {
                // update new list to only include documents that still need fetched
                list = list.filter((document) => {
                    // find the document in the old list
                    const oldItem = state.list.find((oldDocument) => {
                        return document.id === oldDocument.id;
                    });
                    // find the document content
                    const oldContent = state.documents.find((oldContent) => {
                        return oldContent.document_id === document.id;
                    });
                    // check if the last_modified field has changed
                    // check if the document content had ever been fetched
                    const needsUpdated = oldItem.last_modified < document.last_modified, noContent = !oldContent;
                    return needsUpdated || noContent;
                });
                // Commit everything.
                each(list, (item,index) =>{ 
                    commit('addToQueue', item.id);
                });
                dispatch('fetchAllDocuments');
                return Promise.resolve(list);
            } else {
                each(list, (item,index) =>{ 
                    commit('addToQueue', item.id);
                });
                dispatch('fetchAllDocuments');
                return Promise.resolve(list);
            }
        },

        sleep(ms) {
            return new Promise(resolve => setTimeout(resolve, ms));
        },

        async fetchAllDocuments({ state, commit, dispatch, rootState }) {
            // loop through items in recently fetched document list until complete
            if (state.fetching_all) { return Promise.resolve(rootState.online_status); }
            commit('setFetching', true);
            let loop = true;
            while(loop && rootState.online_status) {
                const document = nth(state.downloading, 0);
                if (document) {
                    loop = false;
                    continue;
                }
                await dispatch('fetchDocument', document);
                if (state.downloading.length < 1) {
                    loop = false;
                }
            }
            return Promise.resolve(rootState.online_status);
        },

        async fetchDocument({ state, dispatch, commit }, document_id) {
            try {
                const filename = await dispatch('fetchDocumentPDF', document_id);
                return Promise.resolve(filename);
            } catch(e) {
                return Promise.reject(e);
            }
        },

        async checkDocumentsCache({ state, dispatch, commit, getters, rootState}) {
            commit('setDocumentSyncing', true);
            const info = await Device.getInfo();
            const list = getters.getDocumentsList('preplans');
            const sub = rootState.subscriptions.active_subscription.id;
            for ( const index in list ) {
                const document = list[index];
                const filename = document.id + '.pdf';
                try {
                    // Check that the file exists
                    const stat = await Filesystem.stat({
                        path: filename,
                        directory:  FilesystemDirectory.Cache
                    });
                    const server = ( info.platform === 'web' ) ? document.last_modified * 1000 : document.last_modified;
                    const local = stat.mtime;
                    if (server > local ) { 
                        console.info(`(${document.id}) needs to be updated.`);
                        console.info(`Doc ${document.id}: ${server} vs. ${local}`);
                        let items = state.cached.filter((item) => {
                            return Number.isInteger(item) && item !== document.id;
                        });
                        commit('setCache', items);
                    }
                } catch (err) { 
                    console.error(err);
                    let items = state.cached.filter((item) => {
                        return Number.isInteger(item) && item !== document.id;
                    });
                    console.info(items);
                    commit('setCache', items);
                }
            }
            commit('setDocumentSyncing', false);
            dispatch('fetchMissingDocuments');
        },

        async fetchDocumentPDF({ rootState, commit, dispatch }, document_id) {
            // fetch document from api
            if (document_id === undefined) {
                return Promise.resolve(false);
            }
            const filename = document_id + '.pdf';
            console.warn('Loading PDF...');
            const url = encodeURI(
                `${rootUrl}/account/application/2/subscription_document/${document_id}?url=1`
            );
            const options = {
                headers: {
                    "Authorization": rootState.authorization
                },
            };
            const response = await fetch(url, options);
            console.log('performing data', document_id);
            if (response.ok) {
                await dispatch('fetchDocumentFromURL', { 
                    'id': document_id, 
                    'url': await response.text(), 
                    'filename': filename 
                });
                return Promise.resolve(filename);
            }
            return Promise.reject(`${filename} could not be downloaded`);
        },

        async fetchDocumentURI({ rootState, commit, dispatch }, document_id) {
            const filename = await dispatch('fetchDocumentPDF', document_id);
            try {
                let contents = await Filesystem.readFile({
                    path: filename,
                    directory:  FilesystemDirectory.Cache,
                    encoding: FilesystemEncoding.UTF8
                });
                const uri = contents.data;
                commit('addToCache', { document_id, uri });
                return Promise.resolve(uri);
            } catch(e) {
                console.error(e);
                console.error(`${filename} was not downloaded. Something happened.`);
                return Promise.reject(e);
            }
        },

        async fetchMissingDocuments({ rootState, commit, getters, dispatch, state }) {
            if (state.document_syncing) { return ; }
            console.info("Fetching missing documents...");
            await commit('setDocumentSyncing', true);
            const missing = getters.getMissingDocuments;
            for (const index in missing) {
                const doc = missing[index];
                await dispatch('fetchDocumentURI', missing[index].id);
            }
            await commit('setDocumentSyncing', false);
        },

        async fetchDocumentFromURL({ rootState, commit, dispatch }, document) {
            // Fetch the PDF from the URL, and save it into the filesystem.
            console.log('performing data url', document.url);
            const response = await fetch(document.url);
            if (response.ok) {
                const data = await response.blob();
                await dispatch('savePDFtoStorage', { 
                    'stream': data, 
                    'id': document.id, 
                    'filename': document.filename 
                });
                return Promise.resolve(data);
            }
            // Something went wrong, reject it.
            return Promise.reject(`File ${document} failed to save.`);
        },

        async savePDFtoStorage({ rootState, commit, dispatch }, document) {        
            try {
                // We really need to make sure the file is written before we continue.
                const filename = await new Promise((resolve) => {
                    const reader = new FileReader();
                    reader.onload = async (e) => { 
                        const data = e.target.result;
                        await Filesystem.writeFile({
                            path: document.filename,
                            data: data,
                            directory:  FilesystemDirectory.Cache,
                            encoding: FilesystemEncoding.UTF8
                        });
                        return resolve(data.filename);
                    }
                    commit('removeFromQueue', document.id);
                    reader.readAsDataURL(document.stream);
                });
                return Promise.resolve(filename);
            } catch(e) {
                console.error('Unable to write file', e);
            }
        },

        clearDocumentsData({ state, commit }) {
            // clear saved documents from vuex store
            commit('setDocuments', []);
            commit('setAnnotationsList', []);
            commit('setDirtyDocuments', []);
            commit('setDocumentsList', []);
            commit('setNewDocumentsList', []);
            commit('setOldDocumentsList', []);
            state.queue = 0;
        },

        // used to get document content from an document page
        async getDocumentContent({state, rootState, dispatch}, id) {
            const response = await dispatch('fetchDocumentPDF', id);
            return Promise.resolve(response);
        },
    },

    getters: {
        getDocumentsList: (state, getters) => (category) => {
            // get the documents list
            let items = state.list.filter((document) => {
                let matchesSubscription = document.subscriptions.includes(getters.getActiveSubscription.id);
                let matchesCategory = document.document_category === category;
                return matchesSubscription && matchesCategory;
            });
            // sort it
            items.sort((a, b) => {
                return a.order - b.order;
            });
            // return it
            return items;
        },

        getCachedDocumentsList: (state, getters, rootState)  => {
            const list = getters.getDocumentsList('preplans');
            if (state.cached) {
                let items = list.filter((document) => {
                    return Object.keys(state.cached).includes(String(document.id));
                });
                return items;
            } 
            return [];
        },

        getMissingDocuments: (state, getters) => {
            const list = getters.getDocumentsList('preplans');
            if (state.cached) {
                let items = list.filter((document) => {
                    return !Object.keys(state.cached).includes(String(document.id));
                });
                return items;
            }
            return list;
        },

        getDocumentUri: (state) => {
            return state.pdf_uri;
        },

        getAnnotations: (state) => {
            return state.annotations;
        },

        getAnnotationsBusy: (state) => {
            return state.updater;
        },

        getDocumentSyncing: (state) => {
            return state.document_syncing;
        },

        getAnnotationState: (state) => {
            return state.annotations_queue[0];
        },

        getCurrentDocument: (state) => (id) => {
            // get a specific document
            return state.list.find((document) => {
                return document.id === id;
            });
        },

        getDocumentBusy: (state) => {
            return state.document_busy;
        },

        getFromCache: (state) => (id) => {
            if (state.cached.hasOwnProperty(id)) {
                return state.cached[id];
            }
            return false;
        },

        getDocumentCache: (state) => {
            return state.cached;
        },

        getLastSaved: (state) => {
            return state.last_saved;
        },

        getDirtyAnnotation: (state) => (id) => {
            return state.dirty_annotations.includes(id);
        },

        getDocumentAnnotation: (state) => (id) => {
            // get a specific document
            return find(state.annotations, (o) => { 
                if (!o || !o.document) { return false; }
                return o.document && o.document === id; 
            });
        },
    },
};